import React from 'react';
import { Link } from 'react-router-dom';

const Terms = () => {
  return (
    <div>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              HOA Manager Pro — A UnifiedOps LLC business (d/b/a "HOA Manager Pro")
            </p>
            <p className="text-sm text-blue-200 mt-4">Effective: September 1, 2025</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* 1. Agreement & Parties */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Agreement & Parties</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-4">
              These Terms govern access to and use of the HOA Manager Pro platform, websites, and related services (the "Service"). "Provider," "we," or "us" means UnifiedOps LLC, doing business as HOA Manager Pro. "Customer" means the homeowners' association, association-in-fact, or management company that purchases or activates the Service. "Authorized Users" means individuals Customer authorizes to use the Service (e.g., owners, directors, managers).
            </p>
            <p className="text-gray-700">
              By clicking "I Agree," creating an account, or using the Service, you accept these Terms on your own behalf and, if applicable, on behalf of Customer, and you represent you have authority to bind Customer.
            </p>
          </div>
        </section>

        {/* 2. Accounts & Eligibility */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Accounts & Eligibility</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              Provide accurate information and keep credentials secure. You are responsible for all activity under your account. Customer is responsible for its Authorized Users' compliance.
            </p>
          </div>
        </section>

        {/* 3. Subscriptions, Billing & Taxes */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Subscriptions, Billing & Taxes</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-4">
              Plans are billed monthly or annually. You may cancel any time in-app or by contacting support; access continues through the current paid period. Annual plans: upon cancellation you may request a pro-rated refund of the unused portion effective on your requested termination date. Add-ons adjust on a pro-rated basis. Listed fees exclude applicable taxes, which you are responsible to pay. We may change pricing with notice; changes apply on renewal unless otherwise agreed.
            </p>
          </div>
        </section>

        {/* 4. Payments; Third-Party Processors */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Payments; Third-Party Processors</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              We use third-party payment processors (e.g., Stripe). By paying through the Service, you agree to their terms. We may suspend or terminate for chargebacks, fraud, or unpaid amounts.
            </p>
          </div>
        </section>

        {/* 5. Acceptable Use */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Acceptable Use</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-4">You will not:</p>
            <ul className="space-y-2 text-gray-700 ml-4">
              <li>(a) use the Service unlawfully</li>
              <li>(b) upload malicious code</li>
              <li>(c) interfere with security or integrity</li>
              <li>(d) attempt to access others' data</li>
              <li>(e) reverse engineer or scrape except as permitted by law</li>
              <li>(f) use the Service to harass, defame, or infringe rights</li>
            </ul>
            <p className="text-gray-700 mt-4">We may suspend or limit access for violations.</p>
          </div>
        </section>

        {/* 6. Role of the Service */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Role of the Service; No Legal Advice</h2>
          <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">(a) No Legal Advice</h3>
              <p className="text-gray-700">
                The Service is a software platform that facilitates communication, recordkeeping, voting, payments, and document management. We do not provide legal advice and do not determine eligibility, quorum, or compliance with statutes, bylaws, or covenants. Customer is responsible for adopting any required resolutions (e.g., Fla. Stat. 720.317 electronic voting), setting election rules, validating eligibility, and complying with notice and records laws.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">(b) DIY Platform; No Guarantee of Legal Compliance or Rule Currency</h3>
              <p className="text-gray-700 mb-4">
                The Service is a customizable, do-it-yourself ("DIY") software platform designed to accommodate a wide range of governance models, association structures, and state-specific requirements. Because statutes, administrative rules, and industry standards change over time, the Service may not always reflect the most current legal requirements, formats, or procedures applicable to a particular Association. Updates to the Service—where required by law or industry practice—may require development, testing, and phased implementation, and may not coincide with the effective date of any statutory or regulatory changes.
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Customer and its Board, officers, managers, and Authorized Users are solely responsible for:</strong>
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-2">
                <li>(i) determining what laws, rules, procedures, and governing documents apply;</li>
                <li>(ii) ensuring that any use of the Service complies with applicable statutes, covenants, bylaws, and internal rules; and</li>
                <li>(iii) consulting qualified legal counsel as needed.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Provider does not review, validate, or certify Customer's governance actions or communications for legal compliance and is not responsible for any errors, omissions, or non-compliance arising from Customer's use of the Service.
              </p>
            </div>
          </div>
        </section>

        {/* 7. Data Ownership */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Data Ownership; License to Operate the Service</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              As between the parties, Customer owns its data (including member lists, minutes, votes, proxies, files). You grant us a non-exclusive, worldwide, royalty-free license to host, process, transmit, display, and back up Customer Data solely to provide, secure, support, and improve the Service. We own the Service and all related IP.
            </p>
          </div>
        </section>

        {/* 8. Privacy; Data Protection */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Privacy; Data Protection</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              Our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> describes our data practices. We implement reasonable technical and organizational safeguards. No system is 100% secure and we do not warrant against all incidents. If required by law, we may preserve or disclose data in response to lawful process; when permitted, we will notify Customer.
            </p>
          </div>
        </section>

        {/* 9. Electronic Records & Signatures */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Electronic Records & Signatures; Notices (ESIGN/UETA)</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              You consent to transact electronically and receive notices, records, and disclosures through the Service and by email/SMS where enabled. Electronic signatures and records created through the Service are intended to be valid under ESIGN and UETA. You may withdraw consent as described in the E-Consent; withdrawal may limit features.
            </p>
          </div>
        </section>

        {/* 10. Voting, Proxies, and Audit Logs */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Voting, Proxies, and Audit Logs</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              Electronic votes and proxies cast through the Service are time-stamped and logged. Customer is responsible for rules governing eligibility, proxies (directed/delegated), one-vote-per-property, and cutoffs. We maintain audit logs but do not adjudicate disputes.
            </p>
          </div>
        </section>

        {/* 11. Records; Custodianship */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Records; Custodianship</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              Customer remains the custodian of records unless Customer adopts a resolution designating HOA Manager Pro as an authorized electronic custodian. Customer is responsible for responding to member inspection requests and producing records; the Service provides export tools.
            </p>
          </div>
        </section>

        {/* 12. Sensitive/Prohibited Data */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">12. Sensitive/Prohibited Data</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              Do not upload Social Security numbers, bank account numbers, or health information (PHI) except where strictly necessary and permitted by law. We do not process PHI under HIPAA and are not a Business Associate unless a separate BAA is executed.
            </p>
          </div>
        </section>

        {/* 13. Service Changes */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">13. Service Changes; Beta Features; Availability</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              We may modify or discontinue features on reasonable notice. From time to time we may offer Beta features "as-is," which you use at your discretion. We target high availability but outages and maintenance may occur.
            </p>
          </div>
        </section>

        {/* 14. Warranties & Disclaimers */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">14. Warranties & Disclaimers</h2>
          <div className="bg-white rounded-lg shadow-md p-6 bg-yellow-50 border border-yellow-200">
            <p className="text-gray-900 font-semibold">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </div>
        </section>

        {/* 15. Limitation of Liability */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">15. Limitation of Liability</h2>
          <div className="bg-white rounded-lg shadow-md p-6 bg-yellow-50 border border-yellow-200 space-y-4">
            <p className="text-gray-900 font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES; OUR AGGREGATE LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE AMOUNTS PAID TO US FOR THE SERVICE DURING THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY.
            </p>
            <p className="text-gray-900 font-semibold">
              PROVIDER SHALL NOT BE LIABLE FOR ANY FAILURE BY CUSTOMER TO COMPLY WITH APPLICABLE STATUTES, GOVERNING DOCUMENTS, ELECTION PROCEDURES, RECORD-INSPECTION LAWS, OR NOTICE REQUIREMENTS, REGARDLESS OF WHETHER THE SERVICE PROVIDES TOOLS RELATED TO THOSE FUNCTIONS.
            </p>
          </div>
        </section>

        {/* 16. Indemnification */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">16. Indemnification</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              You (and Customer) will defend and indemnify us from claims arising out of (a) your use of the Service in violation of law or these Terms; (b) Customer's governance decisions, election rules, or record responses; or (c) content you submit.
            </p>
          </div>
        </section>

        {/* 17. Term; Termination */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">17. Term; Termination; Data Export</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              These Terms continue until terminated. Either party may terminate for convenience upon notice effective at period-end or for breach after cure period. On termination, Customer may request a data export for a limited window (e.g., 30–60 days); thereafter we may delete or archive data per our retention policy.
            </p>
          </div>
        </section>

        {/* 18. Dispute Resolution */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">18. Dispute Resolution; Governing Law; Venue</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              These Terms are governed by the laws of the State of Florida, excluding conflict-of-laws rules. The parties consent to exclusive jurisdiction and venue in the state or federal courts located in Escambia County, Florida.
            </p>
          </div>
        </section>

        {/* 19. Miscellaneous */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">19. Miscellaneous</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-4">
              <strong>Force Majeure:</strong> Neither party is liable for delays or failures due to causes beyond reasonable control.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Assignment:</strong> You may not assign without consent; we may assign to an affiliate or successor.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Notices:</strong> Notices may be provided electronically.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Severability:</strong> If any provision is unenforceable, the remainder continues in effect.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Waiver:</strong> No waiver of any provision is effective unless in writing.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Entire Agreement:</strong> These Terms constitute the entire agreement between the parties.
            </p>
            <p className="text-gray-700">
              <strong>Survival:</strong> Sections 6–19 survive termination.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-lg text-white p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Questions About Our Terms?</h2>
          <p className="text-blue-100 mb-2">
            <strong>UnifiedOps LLC</strong> d/b/a HOA Manager Pro
          </p>
          <p className="text-blue-100 mb-6">
            6901A N 9TH AVENUE #699<br/>
            Pensacola, FL 32504, United States
          </p>
          <a
            href="mailto:info@hoamanagerpro.us"
            className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            info@hoamanagerpro.us
          </a>
        </section>

        {/* Back to Home */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
